import { useRef, useState, type FormEvent } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import useLightReveal from '@/hooks/useLightReveal';
import { contactHeroImg, interestOptions } from '@/mocks/expertise';

const FORM_URL = 'https://readdy.ai/api/form/db7ebpgmkl3spn8sm7s0';
type Status = 'idle' | 'sending' | 'success' | 'error';
type Errors = Partial<Record<'first_name' | 'last_name' | 'email' | 'interest' | 'message', string>>;

export default function ContactPage() {
  const ref = useRef<HTMLElement>(null);
  useLightReveal(ref, 'contact');
  const [interest, setInterest] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [count, setCount] = useState(0);

  const pick = (opt: string, scroll: boolean) => {
    setInterest(opt);
    setErrors((e) => ({ ...e, interest: undefined }));
    if (scroll) document.getElementById('inquiry')?.scrollIntoView({ block: 'start' });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    if (String(fd.get('website_alt') || '').trim()) { setStatus('success'); return; }
    fd.delete('website_alt');
    const v = (k: string) => String(fd.get(k) || '').trim();
    const next: Errors = {};
    if (!v('first_name')) next.first_name = 'Required';
    if (!v('last_name')) next.last_name = 'Required';
    if (!v('email')) next.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v('email'))) next.email = 'Enter a valid email';
    if (!v('interest')) next.interest = 'Select an area';
    if (!v('message')) next.message = 'Required';
    else if (v('message').length > 500) next.message = 'Max 500 characters';
    setErrors(next);
    if (Object.keys(next).length) { setStatus('error'); setFormError('Please complete the highlighted fields.'); return; }
    if (!v('organization')) fd.delete('organization');

    const body = new URLSearchParams();
    fd.forEach((val, k) => body.append(k, String(val)));
    setStatus('sending');
    setFormError('');
    try {
      const response = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg || '')) {
        setStatus('success');
        form.reset();
        setInterest('');
        setCount(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Your inquiry could not be sent.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error — your inquiry was not sent.');
    }
  };

  const input = 'w-full bg-transparent border-0 outline-none py-4 text-[15px] text-foreground-950 placeholder:text-foreground-400';
  const row = (err?: string) => `block border-b ${err ? 'border-primary-500' : 'border-foreground-950/20'} focus-within:border-foreground-950 transition-colors pt-4`;
  const lab = (text: string, err?: string, opt?: boolean) => (
    <span className="flex justify-between label-xs text-foreground-600">
      <span>{text}{opt ? ' (optional)' : ' *'}</span>
      {err && <span className="text-primary-500">{err}</span>}
    </span>
  );

  return (
    <>
      <title>Contact | AUREN Management Consulting — Start a Conversation</title>
      <meta name="description" content="Start a conversation with AUREN about strategy and growth, transformation, or leadership and change. Tell us what you are navigating." />
      <Navbar />
      <main ref={ref} className="bg-background-50">
        <section className="relative w-full min-h-[680px] md:min-h-[92vh] overflow-hidden bg-foreground-950 flex items-center">
          <img src={contactHeroImg} alt="Glass architecture at night in deep blue tones" title="AUREN contact" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-secondary-950/40 to-foreground-950/60" />
          <div className="relative w-full wrap pt-28 pb-20 text-center text-white">
            <span className="label-xs text-white/70" data-fade>Contact / AUREN</span>
            <h1 className="font-heading font-light uppercase leading-[0.98] tracking-[0.02em] mt-6 text-[clamp(38px,6.4vw,92px)]" data-fade>Start a<br />conversation.</h1>
            <p className="mt-6 text-[15px] md:text-[16px] leading-[1.6] text-white/80 max-w-[440px] mx-auto" data-fade>Tell us what you&apos;re navigating. We&apos;ll start with a conversation.</p>
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-[900px] mx-auto" role="group" aria-label="Area of interest" data-fade>
              {interestOptions.map((o) => (
                <button key={o} type="button" aria-pressed={interest === o} onClick={() => pick(o, true)} className={`h-14 border label-xs whitespace-nowrap cursor-pointer transition-colors duration-300 ${interest === o ? 'bg-white text-foreground-950 border-white' : 'border-white/50 text-white hover:border-white hover:bg-white/10'}`}>
                  {o}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section id="inquiry" className="relative scroll-mt-14 overflow-hidden py-20 md:py-28">
          <div className="absolute left-1/2 -translate-x-1/2 top-[10%] w-[1000px] max-w-[160vw] h-[700px] contact-glow pointer-events-none" />
          <div className="relative wrap">
            <div className="max-w-[760px] mx-auto">
              <div className="flex items-center justify-between border-b border-foreground-950/15 pb-4" data-fade>
                <span className="label-xs text-foreground-600">Inquiry form</span>
                <span className="label-xs text-foreground-600">* Required</span>
              </div>
              <form id="auren-contact-inquiry" data-readdy-form onSubmit={handleSubmit} noValidate className="mt-6" data-fade>
                <input type="text" name="website_alt" className="inquiry-extra-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                <input type="hidden" name="interest" value={interest} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  <label className={row(errors.first_name)}>{lab('First name', errors.first_name)}<input name="first_name" autoComplete="given-name" className={input} /></label>
                  <label className={row(errors.last_name)}>{lab('Last name', errors.last_name)}<input name="last_name" autoComplete="family-name" className={input} /></label>
                  <label className={row(errors.email)}>{lab('Work email', errors.email)}<input name="email" type="email" autoComplete="email" className={input} /></label>
                  <label className={row()}>{lab('Organization', undefined, true)}<input name="organization" autoComplete="organization" className={input} /></label>
                </div>
                <fieldset className={`${row(errors.interest)} pb-4`}>
                  <legend className="sr-only">Area of interest</legend>
                  {lab('Area of interest', errors.interest)}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
                    {interestOptions.map((o) => (
                      <button key={o} type="button" aria-pressed={interest === o} onClick={() => pick(o, false)} className={`h-11 border label-xs whitespace-nowrap cursor-pointer transition-colors ${interest === o ? 'bg-primary-500 text-background-50 border-primary-500' : 'border-foreground-950/30 text-foreground-950 hover:border-primary-500'}`}>{o}</button>
                    ))}
                  </div>
                </fieldset>
                <label className={row(errors.message)}>
                  {lab('Message', errors.message)}
                  <textarea name="message" maxLength={500} rows={4} onChange={(ev) => setCount(ev.target.value.length)} className={`${input} resize-none`} placeholder="A few lines about the challenge" />
                  <span className="block text-right label-xs text-foreground-400 pb-2">{count}/500</span>
                </label>
                <div className="flex flex-col items-center gap-4 mt-14">
                  <button type="submit" disabled={status === 'sending'} className="group w-[128px] h-[128px] rounded-full border border-foreground-950 flex flex-col items-center justify-center gap-2 text-foreground-950 hover:bg-primary-500 hover:border-primary-500 hover:text-background-50 transition-colors duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60">
                    <span className="label-xs !tracking-[0.18em] text-[10px]">{status === 'sending' ? 'Sending…' : 'Send inquiry'}</span>
                    <i className="ri-arrow-right-line text-lg transition-transform group-hover:translate-x-1" />
                  </button>
                  <div aria-live="polite" className="text-center text-sm min-h-[20px]">
                    {status === 'success' && <p className="text-primary-500">Thank you &mdash; your inquiry has been received.</p>}
                    {status === 'error' && <p className="text-foreground-800" role="alert">{formError}</p>}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}